import {Component, OnDestroy, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {HttpErrorResponse} from '@angular/common/http';
import {Subject} from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import {Standard} from '../../../../../models/standard.model';
import {Question} from '../../../../../models/question.model';
import {Answer} from '../../../../../models/answer.model';
import {AdminService} from '../../../../../services/admin.service';
import {isStandardVersionConflict, savedStandardVersion} from 'app/services/standard-version';

interface EditableQuestion {
  isTesting: boolean;
  answers: Answer[];
  questionText: string;
  rightAnswer: number;
}

interface TemporaryQuestions {
  questions: { russian: Question[]; english: Question[] };
}

const QUESTION_LANGUAGE_RUSSIAN = 'RUSSIAN';
const QUESTION_NUMBER_OFFSET = 1;
const QUESTION_ANSWER_COUNT = 3;
const QUESTION_NOT_FOUND = -1;
const FIRST_QUESTION_CHARACTER = 0;

@Component({
  selector: 'app-edit-question',
  templateUrl: './edit-question.component.html',
  styleUrls: ['./edit-question.component.css']
})
export class EditQuestionComponent implements OnInit, OnDestroy {
  didReload = false;
  standard: Standard;
  tmpStandard: TemporaryQuestions = null;
  questionLang: string;
  newQuestion: EditableQuestion;
  oldQuestion: Question;
  successInfo = false;
  invalidQuestions = false;
  isLoaded = true;
  errorMessage = false;
  versionConflict = false;
  reloadFailed = false;
  private sourceStandard: Standard;
  private sourceVersion: number;
  private sourceQuestion: Question;
  private readonly destroyed$ = new Subject<void>();

  constructor(public activeModal: NgbActiveModal, private adminService: AdminService) {}

  ngOnInit() {}

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  initParams(standard: Standard, question: Question, lang: string): void {
    this.sourceStandard = standard;
    this.sourceVersion = standard && standard.version;
    this.standard = standard && Object.assign({}, standard);
    this.sourceQuestion = question;
    this.oldQuestion = this.tmpStandard ? question : JSON.parse(JSON.stringify(question));
    this.newQuestion = {
      isTesting: question.isTesting,
      answers: question.answers.map(answer => new Answer(answer.answerText)),
      questionText: question.questionText,
      rightAnswer: this.selectIndexOfCorrectAnswer(question)
    };
    this.questionLang = lang;
  }

  initTemporaryParams(question: Question, lang: string, standard: TemporaryQuestions): void {
    this.tmpStandard = standard;
    this.initParams(null, question, lang);
  }

  saveQuestion(): void {
    if (!this.isLoaded || this.versionConflict || (this.errorMessage && !this.tmpStandard)) { return; }
    this.invalidQuestions = !this.isQuestionsValid();
    this.successInfo = false;
    if (this.invalidQuestions) { return; }
    this.isLoaded = false;
    const editedQuestion = this.setCorrectAnswer(this.newQuestion);
    if (this.tmpStandard) {
      this.saveTemporaryQuestion(editedQuestion);
    } else {
      this.saveStoredQuestion(editedQuestion);
    }
  }

  private saveTemporaryQuestion(question: Question): void {
    const russian = this.questionLang.toUpperCase() === QUESTION_LANGUAGE_RUSSIAN;
    const questions = russian ? this.tmpStandard.questions.russian : this.tmpStandard.questions.english;
    const paired = russian ? this.tmpStandard.questions.english : this.tmpStandard.questions.russian;
    const index = questions.indexOf(this.oldQuestion);
    if (index !== QUESTION_NOT_FOUND) {
      questions[index] = question;
      if (paired && paired[index]) { paired[index] = JSON.parse(JSON.stringify(question)); }
      this.oldQuestion = question;
    }
    this.errorMessage = index === QUESTION_NOT_FOUND;
    this.successInfo = !this.errorMessage;
    this.isLoaded = true;
  }

  private saveStoredQuestion(question: Question): void {
    const body = this.combineQuestions(this.oldQuestion, question);
    this.adminService.editQuestionInStandard(body, this.standard, this.questionLang)
      .takeUntil(this.destroyed$).subscribe(() => this.acceptQuestion(question), error => this.rejectSave(error));
  }

  private acceptQuestion(question: Question): void {
    if (this.sourceStandard.version === this.sourceVersion) {
      this.sourceStandard.version = this.standard.version;
      this.sourceVersion = this.standard.version;
      Object.assign(this.sourceQuestion, question);
    }
    this.oldQuestion = question;
    this.errorMessage = false;
    this.successInfo = true;
    this.isLoaded = true;
  }

  private rejectSave(error: HttpErrorResponse): void {
    this.versionConflict = isStandardVersionConflict(error);
    this.errorMessage = !this.versionConflict;
    this.isLoaded = true;
  }

  reloadStandard(): void {
    if (!this.isLoaded || (!this.versionConflict && !this.errorMessage)) { return; }
    this.isLoaded = false;
    this.reloadFailed = false;
    this.adminService.getStandard(this.standard.id).takeUntil(this.destroyed$).subscribe(response => {
      const standard = response.body as Standard;
      const version = savedStandardVersion(response);
      if (version != null) { standard.version = version; }
      this.didReload = true;
      Object.assign(this.sourceStandard, standard);
      this.activeModal.close();
    }, () => {
      this.isLoaded = true;
      this.reloadFailed = true;
    });
  }

  private combineQuestions(oldQuestion: Question, newQuestion: Question): Object {
    return {
      currentQuestionText: oldQuestion.questionText,
      currentAnswers: oldQuestion.answers,
      currentRightAnswer: oldQuestion.rightAnswer,
      newQuestionText: newQuestion.questionText,
      newAnswers: newQuestion.answers,
      newRightAnswer: newQuestion.rightAnswer
    };
  }

  private setCorrectAnswer(question: EditableQuestion): Question {
    return new Question(question.answers, question.isTesting, question.questionText,
      question.answers[Number(question.rightAnswer) - QUESTION_NUMBER_OFFSET]);
  }

  private selectIndexOfCorrectAnswer(question: Question): number {
    return question.answers.findIndex(answer => answer.answerText === question.rightAnswer.answerText) + QUESTION_NUMBER_OFFSET;
  }

  public validateCorrectAnswer(event: {target: HTMLInputElement}): void {
    if (event.target.value.length > QUESTION_NUMBER_OFFSET) { event.target.value = event.target.value[FIRST_QUESTION_CHARACTER]; }
  }

  private isQuestionsValid(): boolean {
    const question = this.newQuestion;
    if (!question.questionText || question.rightAnswer < QUESTION_NUMBER_OFFSET || question.rightAnswer > QUESTION_ANSWER_COUNT) {
      return false;
    }
    const answers = question.answers.map(answer => answer.answerText);
    return answers.every(answer => !!answer) && new Set(answers).size === answers.length;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/admin/create-operating-standard/opst-step-eight/edit-question-step-popup/edit-question.component.ts